import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useConfirm } from '../components/ui/ConfirmDialog';
import {
  changeLifecycle, changePlan, getBusiness, getPlans, PlatformApiError,
  type BusinessDetail, type Lifecycle, type PlanOption,
} from './api';
import { navigatePlatform } from './PlatformApp';
import { PlanChangeModal } from './PlanChangeModal';
import { ReasonDialog } from './ReasonDialog';
import { AUDIT_LABEL, auditSummary, usageText } from './format';

const fmtDateTime = (iso: string) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
const lifecycleBadge = (l: Lifecycle) => (l === 'ACTIVE' ? <StatusBadge variant="success">Activo</StatusBadge> : l === 'SUSPENDED' ? <StatusBadge variant="warning">Suspendido</StatusBadge> : <StatusBadge variant="neutral">Archivado</StatusBadge>);

export function BusinessDetailPage({ businessId }: { businessId: string }) {
  const confirm = useConfirm();
  const [detail, setDetail] = useState<BusinessDetail | null>(null);
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [planModal, setPlanModal] = useState(false);
  // Accion de lifecycle que requiere motivo (suspender/archivar) pendiente de confirmar en el dialogo.
  const [reasonAction, setReasonAction] = useState<{ target: Lifecycle; title: string; description: string; confirmLabel: string; danger?: boolean } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(undefined);
    try { setDetail(await getBusiness(businessId)); }
    catch (e) { setError(e instanceof PlatformApiError && e.status === 404 ? 'El negocio no existe.' : 'No fue posible cargar el negocio.'); }
    finally { setLoading(false); }
  }, [businessId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { getPlans().then((r) => setPlans(r.plans)).catch(() => setPlans([])); }, []);

  const applyLifecycle = async (target: Lifecycle, reason?: string) => {
    setBusy(true); setError(undefined); setNotice(undefined);
    try {
      const updated = await changeLifecycle(businessId, target, reason);
      setDetail(updated); setNotice('Estado actualizado.'); setReasonAction(null);
    } catch (e) { setError(e instanceof PlatformApiError ? e.message : 'No fue posible cambiar el estado.'); }
    finally { setBusy(false); }
  };

  // Suspender/Archivar => dialogo con motivo. Reactivar/Restaurar => confirmacion simple (sin motivo).
  const doLifecycle = async (target: Lifecycle, opts: { title: string; description: string; danger?: boolean; needReason: boolean }) => {
    if (opts.needReason) {
      setReasonAction({ target, title: opts.title, description: opts.description, confirmLabel: opts.title, danger: opts.danger });
      return;
    }
    if (!(await confirm({ title: opts.title, description: opts.description, confirmLabel: opts.title, danger: opts.danger }))) return;
    await applyLifecycle(target);
  };

  if (loading) return <div className="inline-loading"><span className="spinner" />Cargando…</div>;
  if (error && !detail) return (
    <div>
      <button className="text-button" onClick={() => navigatePlatform('#platform')} type="button">← Volver</button>
      <div className="alert alert--error" role="alert">{error}</div>
    </div>
  );
  if (!detail) return null;

  const d = detail;

  return (
    <section aria-label={`Negocio ${d.name}`}>
      <div className="platform-detail__top"><button className="text-button" onClick={() => navigatePlatform('#platform')} type="button">← Volver a negocios</button></div>
      <PageHeader
        title={<>{d.name} {lifecycleBadge(d.lifecycle)}</>}
        description={<>{d.slug} · Creado {fmtDateTime(d.createdAt)}</>}
        actions={<LifecycleActions lifecycle={d.lifecycle} busy={busy} onAction={doLifecycle} />}
      />

      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {notice && <div className="alert alert--success" role="status">{notice}</div>}
      {d.planMissing && <div className="alert alert--error" role="alert">Este negocio no tiene un plan válido asignado. Asigna un plan para regularizarlo.</div>}
      {d.usage && (d.usage.stores.overLimit || d.usage.users.overLimit) && (
        <div className="alert alert--warning" role="alert">Este negocio está por encima del límite de su plan. No podrá aumentar ese recurso hasta volver dentro del límite.</div>
      )}

      <div className="platform-detail__grid">
        <div className="platform-panel">
          <div className="platform-panel__head"><h3>Plan y uso</h3><button className="button button--secondary" disabled={busy} onClick={() => setPlanModal(true)} type="button">Cambiar plan</button></div>
          <dl className="platform-kv">
            <div><dt>Plan actual</dt><dd>{d.plan ? d.plan.name : <StatusBadge variant="danger">Sin plan</StatusBadge>}</dd></div>
            {d.plan && <div><dt>Vigente desde</dt><dd>{fmtDateTime(d.plan.effectiveFrom)}</dd></div>}
            {d.usage && <div><dt>Tiendas</dt><dd className={d.usage.stores.overLimit ? 'platform-over' : ''}>{usageText(d.usage.stores)}</dd></div>}
            {d.usage && <div><dt>Usuarios</dt><dd className={d.usage.users.overLimit ? 'platform-over' : ''}>{usageText(d.usage.users)}</dd></div>}
          </dl>
          {d.usage && d.usage.entitlements.some((e) => e.kind === 'BOOLEAN') && (
            <div className="platform-features">
              {d.usage.entitlements.filter((e) => e.kind === 'BOOLEAN').map((e) => (
                <StatusBadge key={e.key} variant={e.enabled ? 'success' : 'neutral'}>{e.label}: {e.enabled ? 'Sí' : 'No'}</StatusBadge>
              ))}
            </div>
          )}
        </div>

        <div className="platform-panel">
          <h3>Integraciones</h3>
          <dl className="platform-kv">
            <div><dt>Loyverse</dt><dd>{d.integrations.loyverseConnected ? <StatusBadge variant="success">Conectado</StatusBadge> : <StatusBadge variant="neutral">No conectado</StatusBadge>}</dd></div>
          </dl>
        </div>
      </div>

      <div className="platform-detail__grid">
        <div className="platform-panel">
          <h3>Tiendas ({d.stores.length})</h3>
          {d.stores.length === 0 ? <p className="platform-muted">Sin tiendas.</p> : (
            <ul className="platform-list">
              {d.stores.map((s) => <li key={s.id}><span>{s.name}</span>{s.isActive ? <StatusBadge variant="success">Activa</StatusBadge> : <StatusBadge variant="neutral">Inactiva</StatusBadge>}</li>)}
            </ul>
          )}
        </div>
        <div className="platform-panel">
          <h3>Usuarios ({d.users.length})</h3>
          {d.users.length === 0 ? <p className="platform-muted">Sin usuarios.</p> : (
            <ul className="platform-list">
              {d.users.map((u) => <li key={u.id}><span>{u.name} <small>{u.email} · {u.role}</small></span>{u.isActive ? <StatusBadge variant="success">Activo</StatusBadge> : <StatusBadge variant="neutral">Deshabilitado</StatusBadge>}</li>)}
            </ul>
          )}
        </div>
      </div>

      <div className="platform-panel">
        <h3>Historial administrativo</h3>
        {d.audit.length === 0 ? <p className="platform-muted">Sin acciones registradas.</p> : (
          <ul className="platform-timeline">
            {d.audit.map((e) => (
              <li key={e.id}>
                <div className="platform-timeline__head"><strong>{AUDIT_LABEL[e.action] ?? e.action}</strong><span>{fmtDateTime(e.createdAt)}</span></div>
                <div className="platform-timeline__body">{auditSummary(e)}</div>
                <div className="platform-timeline__meta">{e.actorName}{e.reason ? ` · ${e.reason}` : ''}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {reasonAction && (
        <ReasonDialog
          title={reasonAction.title}
          description={reasonAction.description}
          confirmLabel={reasonAction.confirmLabel}
          danger={reasonAction.danger}
          busy={busy}
          onCancel={() => setReasonAction(null)}
          onConfirm={(reason) => void applyLifecycle(reasonAction.target, reason)}
        />
      )}

      {planModal && d.usage && (
        <PlanChangeModal
          plans={plans}
          current={d.plan?.code ?? null}
          usage={d.usage}
          busy={busy}
          onClose={() => setPlanModal(false)}
          onConfirm={async (planCode, reason) => {
            setBusy(true); setError(undefined); setNotice(undefined);
            try { const updated = await changePlan(businessId, planCode, reason); setDetail(updated); setNotice('Plan actualizado.'); setPlanModal(false); }
            catch (e) { setError(e instanceof PlatformApiError ? e.message : 'No fue posible cambiar el plan.'); }
            finally { setBusy(false); }
          }}
        />
      )}
    </section>
  );
}

function LifecycleActions({ lifecycle, busy, onAction }: { lifecycle: Lifecycle; busy: boolean; onAction: (t: Lifecycle, o: { title: string; description: string; danger?: boolean; needReason: boolean }) => void }) {
  if (lifecycle === 'ACTIVE') return (
    <>
      <button className="button button--secondary" disabled={busy} onClick={() => onAction('SUSPENDED', { title: 'Suspender negocio', description: 'Los usuarios del negocio no podrán operar mientras la cuenta esté suspendida. Los datos se conservarán.', needReason: true })} type="button">Suspender</button>
      <button className="button button--danger" disabled={busy} onClick={() => onAction('ARCHIVED', { title: 'Archivar negocio', description: 'Este negocio quedará fuera de operación normal. Sus datos se conservarán.', danger: true, needReason: true })} type="button">Archivar</button>
    </>
  );
  if (lifecycle === 'SUSPENDED') return (
    <>
      <button className="button button--primary" disabled={busy} onClick={() => onAction('ACTIVE', { title: 'Reactivar negocio', description: 'El negocio volverá a estado ACTIVE y podrá operar.', needReason: false })} type="button">Reactivar</button>
      <button className="button button--danger" disabled={busy} onClick={() => onAction('ARCHIVED', { title: 'Archivar negocio', description: 'Este negocio quedará fuera de operación normal. Sus datos se conservarán.', danger: true, needReason: true })} type="button">Archivar</button>
    </>
  );
  return <button className="button button--primary" disabled={busy} onClick={() => onAction('ACTIVE', { title: 'Restaurar negocio', description: 'Restaurará el negocio a estado ACTIVE. Su plan y datos se conservan.', needReason: false })} type="button">Restaurar</button>;
}
